import { useState, useEffect } from 'react';
import { Outlet, Link, useParams, useLocation } from 'react-router-dom';
import {
  LayoutGrid,
  List,
  Settings,
  ChevronRight,
  Folder,
  Home,
  User,
  GanttChart,
  Plus,
  RefreshCw,
  Boxes,
  Search,
  FileText,
  BarChart3,
  Eye,
  Inbox,
  Zap,
  LogOut,
} from 'lucide-react';
import clsx from 'clsx';
import { CreateIssueModal } from './CreateIssueModal';
import { SearchModal } from './SearchModal';
import { IssuePeekOverview } from './IssuePeekOverview';
import { useAuth } from './AuthProvider';

export function Layout() {
  const { workspaceId, projectId } = useParams();
  const location = useLocation();
  const { user, isAuthenticated, isLoading, login, logout } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);

  // Global keyboard shortcuts - must be before any conditional returns
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in input/textarea or if modal is already open
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        showCreateModal ||
        showSearchModal
      ) {
        return;
      }

      // C key to open create modal (only when in a project)
      if (e.key === 'c' && !e.metaKey && !e.ctrlKey && !e.altKey && projectId && workspaceId) {
        e.preventDefault();
        setShowCreateModal(true);
      }

      // / or Cmd+K to open search modal (only when in a project)
      if ((e.key === '/' || (e.metaKey && e.key === 'k')) && projectId) {
        e.preventDefault();
        setShowSearchModal(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [projectId, workspaceId, showCreateModal, showSearchModal]);

  const isActiveRoute = (path: string) => {
    return location.pathname.includes(path);
  };

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <div className="w-16 h-16 bg-primary-600 rounded-xl flex items-center justify-center mx-auto mb-6">
            <span className="text-white font-bold text-3xl">P</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">PM Redux</h1>
          <p className="text-gray-500 mb-8">Sign in to access your workspaces and projects</p>
          <button
            onClick={login}
            className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Sign in with Microsoft
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-gray-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">P</span>
            </div>
            <span className="font-semibold text-gray-900">PM Redux</span>
            <span className="px-2 py-0.5 text-xs font-semibold bg-green-600 text-white rounded">
              DEV
            </span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          <Link
            to="/workspaces"
            className={clsx(
              'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              location.pathname === '/workspaces'
                ? 'bg-primary-50 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100'
            )}
          >
            <Home className="w-5 h-5" />
            Workspaces
          </Link>

          {workspaceId && projectId && (
            <>
              <div className="pt-4 pb-2">
                <span className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Project Views
                </span>
              </div>

              <Link
                to={`/workspaces/${workspaceId}/projects/${projectId}/board`}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActiveRoute('/board')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                <LayoutGrid className="w-5 h-5" />
                Board
              </Link>

              <Link
                to={`/workspaces/${workspaceId}/projects/${projectId}/list`}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActiveRoute('/list')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                <List className="w-5 h-5" />
                List
              </Link>

              <Link
                to={`/workspaces/${workspaceId}/projects/${projectId}/gantt`}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActiveRoute('/gantt')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                <GanttChart className="w-5 h-5" />
                Gantt
              </Link>

              <div className="pt-4 pb-2">
                <span className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Planning
                </span>
              </div>

              <Link
                to={`/workspaces/${workspaceId}/projects/${projectId}/cycles`}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActiveRoute('/cycles')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                <RefreshCw className="w-5 h-5" />
                Cycles
              </Link>

              <Link
                to={`/workspaces/${workspaceId}/projects/${projectId}/modules`}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActiveRoute('/modules')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                <Boxes className="w-5 h-5" />
                Modules
              </Link>

              <Link
                to={`/workspaces/${workspaceId}/projects/${projectId}/epics`}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActiveRoute('/epics')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                <Zap className="w-5 h-5" />
                Epics
              </Link>

              <div className="pt-4 pb-2">
                <span className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Documentation
                </span>
              </div>

              <Link
                to={`/workspaces/${workspaceId}/projects/${projectId}/pages`}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActiveRoute('/pages')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                <FileText className="w-5 h-5" />
                Pages
              </Link>

              <div className="pt-4 pb-2">
                <span className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Insights
                </span>
              </div>

              <Link
                to={`/workspaces/${workspaceId}/projects/${projectId}/views`}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActiveRoute('/views')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                <Eye className="w-5 h-5" />
                Views
              </Link>

              <Link
                to={`/workspaces/${workspaceId}/projects/${projectId}/analytics`}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActiveRoute('/analytics')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                <BarChart3 className="w-5 h-5" />
                Analytics
              </Link>

              <Link
                to={`/workspaces/${workspaceId}/projects/${projectId}/inbox`}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActiveRoute('/inbox')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                <Inbox className="w-5 h-5" />
                Inbox
              </Link>

              <Link
                to={`/workspaces/${workspaceId}/projects/${projectId}/settings`}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActiveRoute('/settings')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                <Settings className="w-5 h-5" />
                Settings
              </Link>
            </>
          )}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center gap-3">
            {user?.photoUrl ? (
              <img
                src={user.photoUrl}
                alt={user.displayName}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <User className="w-4 h-4 text-primary-700" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.displayName || 'Guest'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email || 'Not signed in'}
              </p>
            </div>
            <button
              onClick={logout}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col">
        {/* Header with breadcrumb and create button */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/workspaces" className="text-gray-500 hover:text-gray-700">
              Workspaces
            </Link>
            {workspaceId && (
              <>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <Link
                  to={`/workspaces/${workspaceId}`}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <Folder className="w-4 h-4 inline mr-1" />
                  Workspace
                </Link>
              </>
            )}
            {projectId && (
              <>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className="text-gray-900 font-medium">Project</span>
              </>
            )}
          </nav>

          {/* Actions - only show when in a project */}
          {projectId && workspaceId && (
            <div className="flex items-center gap-3">
              {/* Search Button */}
              <button
                onClick={() => setShowSearchModal(true)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-600"
              >
                <Search className="w-4 h-4" />
                <span>Search</span>
                <kbd className="ml-2 px-1.5 py-0.5 bg-gray-100 rounded text-xs">/</kbd>
              </button>

              {/* Create Issue Button */}
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                <span>Create Issue</span>
                <kbd className="ml-2 px-1.5 py-0.5 bg-primary-500 rounded text-xs">C</kbd>
              </button>
            </div>
          )}
        </header>

        {/* Page content - overflow hidden so each page manages its own scroll */}
        <div className="flex-1 overflow-hidden">
          <Outlet />
        </div>
      </main>

      {/* Global Create Issue Modal */}
      {showCreateModal && projectId && workspaceId && (
        <CreateIssueModal
          projectId={projectId}
          workspaceId={workspaceId}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Global Search Modal */}
      {showSearchModal && projectId && (
        <SearchModal
          projectId={projectId}
          onClose={() => setShowSearchModal(false)}
          onSelectIssue={(issueId) => setSelectedIssueId(issueId)}
        />
      )}

      {/* Issue Peek Overview from Search */}
      {selectedIssueId && projectId && (
        <IssuePeekOverview
          issueId={selectedIssueId}
          projectId={projectId}
          onClose={() => setSelectedIssueId(null)}
        />
      )}
    </div>
  );
}
